import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { BRAND } from "@/lib/brand";
import { useAuth } from "@/context/AuthContext";
import { Ambulance, CheckCircle2 } from "lucide-react";

const PASSWORD_MIN_LENGTH = 10;

function passwordError(password, confirmPassword) {
  if (password.length < PASSWORD_MIN_LENGTH) return `At least ${PASSWORD_MIN_LENGTH} characters, with a letter and a number.`;
  if (!/[A-Za-z]/.test(password) || !/[0-9]/.test(password)) return "At least 10 characters, with a letter and a number.";
  if (password !== confirmPassword) return "Passwords do not match.";
  return "";
}

export default function InviteAccept() {
  const { token } = useParams();
  const nav = useNavigate();
  const [invite, setInvite] = useState(null);
  const [err, setErr] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    api
      .get(`/invites/lookup/${token}`)
      .then((r) => setInvite(r.data))
      .catch((e) => setErr(formatApiError(e.response?.data?.detail) || e.message));
  }, [token]);

  const pwError = password || confirmPassword ? passwordError(password, confirmPassword) : "";
  const canSubmit = !pwError && password.length >= PASSWORD_MIN_LENGTH;

  const accept = async () => {
    setAccepting(true);
    setErr("");
    try {
      const { data } = await api.post("/invites/accept", {
        token,
        new_password: password,
      });
      localStorage.setItem("nemt_token", data.access_token);
      localStorage.setItem("nemt_user", JSON.stringify(data.user));
      // Reload so AuthContext re-syncs
      window.location.href = "/app";
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
      setAccepting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F4F6F8] flex items-center justify-center p-6">
      <div className="w-full max-w-md bg-white rounded-lg border border-slate-200 p-8" data-testid="invite-page">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-md bg-[#0A192F] flex items-center justify-center">
            <Ambulance className="text-white" size={20} />
          </div>
          <div>
            <div className="font-display font-semibold text-lg">{BRAND}</div>
            <div className="text-xs text-slate-500">Portal invitation</div>
          </div>
        </div>

        {err && !invite && (
          <div className="text-sm text-red-700 bg-red-50 border border-red-100 rounded-md px-3 py-2" data-testid="invite-error">{err}</div>
        )}

        {invite && (
          <>
            <div className="text-xs font-semibold uppercase tracking-[0.14em] text-[#005E82]">Welcome</div>
            <h1 className="font-display text-2xl font-bold text-[#0A192F] mt-1">
              You've been invited to <span className="text-[#005E82]">{invite.company_name}</span>
            </h1>
            <p className="text-sm text-slate-600 mt-2">
              Create your password below to activate your account. You will be able to submit rides directly from your portal.
            </p>

            <div className="mt-6 p-4 rounded-md bg-slate-50 border border-slate-200 space-y-3 text-sm">
              <div><span className="text-slate-500 text-xs uppercase tracking-wider font-semibold">Email:</span> <div className="font-medium">{invite.email}</div></div>
              <div className="pt-2 border-t border-slate-200 space-y-2">
                <div>
                  <label className="text-xs text-slate-500 font-medium">Create a password</label>
                  <input
                    data-testid="invite-password"
                    type="password"
                    autoComplete="new-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="mt-1 w-full text-sm px-3 py-2 rounded-md border border-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-300"
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-500 font-medium">Confirm password</label>
                  <input
                    data-testid="invite-password-confirm"
                    type="password"
                    autoComplete="new-password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="mt-1 w-full text-sm px-3 py-2 rounded-md border border-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-300"
                  />
                </div>
                <div className="text-xs text-slate-500">At least 10 characters, with a letter and a number.</div>
              </div>
            </div>

            {pwError && (
              <div className="mt-4 text-sm text-red-700 bg-red-50 border border-red-100 rounded-md px-3 py-2">{pwError}</div>
            )}

            {err && (
              <div className="mt-4 text-sm text-red-700 bg-red-50 border border-red-100 rounded-md px-3 py-2">{err}</div>
            )}

            <button
              onClick={accept}
              disabled={accepting || !canSubmit}
              className="w-full btn-primary mt-6 flex items-center justify-center gap-2 disabled:opacity-60"
              data-testid="accept-invite-btn"
            >
              <CheckCircle2 size={16} /> {accepting ? "Activating…" : "Activate portal access"}
            </button>
            <div className="mt-3 text-xs text-slate-500 text-center">
              Already have access? <button className="text-[#005E82] hover:underline" onClick={() => nav("/login")}>Sign in</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
